<script  module>
  import AppLayout from '$lib/layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script lang="ts">
  import { router, usePage } from "@inertiajs/svelte";
  import { 
    Upload, 
    Copy, 
    Trash2, 
    Image as ImageIcon, 
    Loader2,
    Check,
    FileIcon,
    Images,
    Plus,
  } from "lucide-svelte";
  import { toast } from "svelte-sonner";
  import { appStore } from '$lib/stores/appStore';
  import { Button } from "$lib/components/ui/button";

  interface MediaItem {
    id: number;
    file_name: string;
    url: string;
    original_url: string;
    file_type: string;
    file_size: number;
  }

  let { medias = [] }: { medias: MediaItem[] } = $props();

  let isUploading = $state(false);
  let uploadProgress = $state(0);

  const page = usePage();
  
  // 💡 动态提取多语言翻译上下文
  const translations = $derived((page.props as any).translations || {});
  const t = $derived(translations.medias || {});


  
  $effect(() => {
    const title = t.title;
    appStore.setTitle(title);
  });

  /**
   * 批量上传处理
   */
  async function handleUpload(e: Event) {
    const target = e.target as HTMLInputElement;
    const files = target.files;
    if (!files || files.length === 0) return;

    isUploading = true;
    uploadProgress = 0;

    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append("media[files][]", file);
    });

    router.post("/medias", formData, {
      forceFormData: true,
      onProgress: (event) => {
        if (event.percentage) {
          uploadProgress = event.percentage;
        }
      },
      onSuccess: () => {
        target.value = ""; 
        const successMsg = t.toast_upload_success?.replace("%{count}", files.length.toString()) || `Uploaded ${files.length} files`;
        toast.success(successMsg);
      },
      onError: (errors) => {
        toast.error(Object.values(errors).join(", "));
      },
      onFinish: () => {
        isUploading = false;
        uploadProgress = 0;
      }
    });
  }

  function copyUrl(url: string) {
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      toast.success(t.toast_copy_success || "Copied", { icon: Check });
    });
  }

  function deleteMedia(id: number) {
    if (!confirm(t.confirm_delete || "Are you sure?")) return;

    router.delete(`/medias/${id}`, {
      onSuccess: () => toast.success(t.toast_delete_success || "Deleted"),
      onError: () => toast.error(t.toast_delete_error || "Error")
    });
  }

  function formatSize(bytes: any): string {
    const num = Number(bytes);
    if (!num || isNaN(num)) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(num) / Math.log(k));
    return parseFloat((num / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
</script>

<svelte:head>
  <title>{t.title || '素材库'}</title>
</svelte:head>

<div class="p-8 max-w-7xl mx-auto">
  <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">

    <header class="flex justify-between items-center mb-8 dark:border-zinc-800 pb-6">
    <div>
      <h1 class="text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 flex items-center gap-2.5">
        <Images class="size-5 text-zinc-800 dark:text-zinc-200" /> {t.title}
      </h1>
      <p class="text-sm text-muted-foreground mt-1">{t.subtitle}</p>
    </div>
  </header>

    


    <div class="flex flex-col items-end gap-2">
      <div class="flex items-center gap-3">
        {#if isUploading}
          <div class="flex items-center gap-2 text-sm text-blue-600 font-medium animate-pulse">
            <Loader2 class="size-4 animate-spin" />
            {t.status_transferring} {uploadProgress}%
          </div>
        {/if}

        <input 
          type="file" 
          onchange={handleUpload} 
          class="hidden" 
          id="file-upload" 
          accept="image/*,video/*"
          disabled={isUploading}
          multiple
        />
        
        <label 
          for="file-upload" 
          class="inline-flex items-center gap-2 px-6 py-2.5 bg-zinc-900 dark:bg-zinc-100 text-zinc-100 dark:text-zinc-900 rounded-xl hover:opacity-90 transition-all cursor-pointer disabled:opacity-50 active:scale-95 shadow-sm"
        >
          <Upload class="size-4" />
          {isUploading ? t.status_processing : t.btn_upload}
        </label>
      </div>
      
      {#if isUploading}
        <div class="w-48 h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
          <div class="bg-blue-600 h-full transition-all duration-300" style="width: {uploadProgress}%"></div>
        </div>
      {/if}
    </div>
  </div>

  {#if medias.length === 0}
    <div class="flex flex-col items-center justify-center py-32 border-2 border-dashed border-zinc-200 dark:border-zinc-800 rounded-[2rem] bg-zinc-50/50 dark:bg-zinc-900/50">
      <div class="p-4 bg-white dark:bg-zinc-800 rounded-2xl shadow-sm mb-4">
        <ImageIcon class="size-10 text-zinc-400" />
      </div>
      <p class="text-lg font-semibold text-zinc-900 dark:text-zinc-100">{t.empty_title}</p>
      <p class="text-sm text-muted-foreground mt-1">{t.empty_desc}</p>
    </div>
  {:else}
    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
      {#each medias as media (media.id)}
        <div class="group flex flex-col bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm hover:shadow-xl transition-all duration-300">
          <div class="relative aspect-square rounded-t-2xl overflow-hidden bg-zinc-100 dark:bg-zinc-950">
            {#if media.file_type?.startsWith('image')}
              <img 
                src={media.url} 
                alt={media.file_name} 
                class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
            {:else}
              <div class="w-full h-full flex items-center justify-center">
                <FileIcon class="size-12 text-zinc-300" />
              </div>
            {/if}
            
            <div class="absolute inset-0 bg-zinc-900/60 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-all duration-200 flex flex-col items-center justify-center gap-3 px-4">
              <button 
                onclick={() => copyUrl(media.original_url)}
                class="w-full flex items-center justify-center gap-2 bg-white text-zinc-900 py-2.5 rounded-xl text-sm font-bold hover:bg-zinc-100 active:scale-95 transition-all shadow-lg"
              >
                <Copy class="size-4" />
                {t.action_copy}
              </button>
              <button 
                onclick={() => deleteMedia(media.id)}
                class="w-full flex items-center justify-center gap-2 bg-red-500 text-white py-2.5 rounded-xl text-sm font-bold hover:bg-red-600 active:scale-95 transition-all shadow-lg"
              >
                <Trash2 class="size-4" />
                {t.action_delete}
              </button>
            </div>
          </div>

          <div class="p-4">
            <h3 class="text-sm font-bold truncate text-zinc-800 dark:text-zinc-200" title={media.file_name}>
              {media.file_name}
            </h3>
            <div class="flex items-center gap-2 mt-1.5">
              <span class="text-[10px] font-bold bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-500 uppercase tracking-tighter">
                {media.file_type?.split('/')[1] || 'FILE'}
              </span>
              <span class="text-[10px] text-zinc-400 font-medium">
                {formatSize(media.file_size)}
              </span>
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>